import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  compose,
  setDisplayName,
} from 'recompose';
import {
  get,
  orderBy,
} from 'lodash';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { withStyles } from '@material-ui/core/styles';

import immutableToJS from 'helpers/immutableToJS';
import {
  dispatchRestMaker,
  formatPrice,
  includesSearchText,
  toLongDateFormat,
  toLongDateFormatWithOutComma,
} from 'helpers/utils';
import {
  Avatar,
  HeaderTitle,
  HighlightText,
  injectIntl,
  Paper,
  SearchComponent,
  SmartTable,
} from 'components';
import ordersActions from 'redux/orders/actions';
import { selectState } from 'redux/selectors';
import transactionsActions from 'redux/transactions/actions';
import InvoiceDetail from 'routes/Calendar/components/InvoiceDetail';

const styles = () => ({
  dialog: {
    maxWidth: 'none',
    width: '95%',
  },
  title: { padding: 0 },
});

class Transactions extends Component {
  static contextTypes = {
    currentUser: PropTypes.shape({}),
    isDistributor: PropTypes.bool,
    isFarmer: PropTypes.bool,
    isRestaurant: PropTypes.bool,
  };

  static propTypes = {
    classes: PropTypes.shape({}).isRequired,
    order: PropTypes.shape({
      id: PropTypes.number,
    }).isRequired,
    orderRequesting: PropTypes.bool,
    transactions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
      }),
    ).isRequired,
    transactionsRequesting: PropTypes.bool,

    listTransactions: PropTypes.func.isRequired,
    formatMessage: PropTypes.func.isRequired,
    readOrder: PropTypes.func.isRequired,
    updateOrder: PropTypes.func.isRequired,
  };

  static defaultProps = {
    orderRequesting: false,
    transactionsRequesting: false,
  }

  state = {
    open: false,
    searchText: '',
  };

  componentWillMount() {
    const { listTransactions } = this.props;
    listTransactions();
  }

  onCancelSearch = () => this.setState({ searchText: '' });

  onSearchText = value => this.setState({ searchText: value });

  getRowClassName = (row, index, data) => {
    const { isFarmer } = this.context;
    if (!isFarmer) return '';
    if (index === 0 || row.draftId !== data[index - 1].draftId) return 'border-top';
    return '';
  }

  getTransactions = () => {
    const { transactions } = this.props;
    const { searchText } = this.state;
    const orderedTransactions = orderBy(
      transactions,
      ['createdAt'],
      ['desc'],
    );
    const transactionsFiltered = orderedTransactions.filter(subItem => includesSearchText(
      searchText,
      [
        `${subItem.orderId}`,
        subItem.buyerName,
        subItem.companyName,
      ],
    ));

    return transactionsFiltered;
  }

  handleClose = () => {
    this.setState({ open: false });
  }

  handleOpen = (orderId) => {
    this.readOrder(orderId);
    this.setState({ open: true });
  };

  readOrder = (orderId) => {
    const { readOrder } = this.props;
    readOrder(orderId);
  }

  render() {
    const {
      currentUser,
      isDistributor,
      isFarmer,
    } = this.context;
    const {
      classes,
      order,
      orderRequesting,
      formatMessage,
      transactionsRequesting,
      updateOrder,
    } = this.props;
    const { open, searchText } = this.state;
    const dialogTitle = (
      <div className="dialog-title">
        <ListItem style={{ pointerEvents: 'none' }}>
          <Avatar
            fullName={get(currentUser, 'account.profile.companyName')}
            src={get(currentUser, 'account.profile.logoUrl')}
            size={40}
            type="SO"
          />
          <ListItemText
            primary={get(order, 'buyer.account.profile.companyName')}
            secondary={`${get(order, 'buyer.account.email')} ${toLongDateFormat(order.updatedAt)}`}
          />
        </ListItem>
      </div>
    );
    const actions = [
      <Button
        color="primary"
        key="close"
        onClick={this.handleClose}
        variant="text"
      >
        Cancel
      </Button>,
      <Button
        color="primary"
        key="okay"
        onClick={this.handleClose}
        variant="text"
      >
        OK
      </Button>,
    ];
    const transactionsFiltered = this.getTransactions();
    const statusArray = {
      authorized: 'Pending',
      captured: 'Paid',
      failure: 'Failure',
    };
    const modifiedTransactions = transactionsFiltered.map(transaction => ({
      ...transaction,
      status: statusArray[transaction.status],
    }));
    const fields = [];
    fields.push({
      label: formatMessage('Delivery Date'),
      name: 'deliveryDate',
      render: row => toLongDateFormatWithOutComma(row.deliveryDate),
      title: row => toLongDateFormatWithOutComma(row.deliveryDate),
    });
    fields.push({
      colSpan: 2,
      label: formatMessage('Customer'),
      name: 'buyerName',
      render: row => (
        <span>
          <HighlightText
            highlight={searchText}
            text={row.buyerName}
          />
        </span>
      ),
      title: row => row.buyerName,
    });
    if (isFarmer) {
      fields.push({
        colSpan: 1,
        label: formatMessage('ID'),
        name: 'draftId',
        render: row => (
          <span
            className="id-pane blue"
            onClick={() => this.handleOpen(row.draftId)}
          >
            <HighlightText
              highlight={searchText}
              text={`${row.draftId}`}
            />
          </span>),
        title: row => row.draftId,
      });
    }
    fields.push({
      colSpan: 1,
      label: formatMessage('Transaction ID'),
      name: 'orderId',
      render: row => (
        <span
          className="id-pane blue"
          onClick={() => this.handleOpen(row.orderId)}
        >
          <HighlightText
            highlight={searchText}
            text={`${row.orderId}`}
          />
        </span>),
      title: row => row.orderId,
    });
    if (isDistributor) {
      fields.push({
        label: formatMessage('Net Total'),
        name: 'net',
        render: row => (
          <span>
            <span className="net-bold">
              {`$${formatPrice(row.amount)} `}
            </span>
            USD
          </span>),
        title: row => `$${formatPrice(row.amount)} USD`,
      });
      fields.push({
        label: formatMessage('Gross Net'),
        name: 'amount',
        render: row => `$${formatPrice(parseFloat(row.amount, 10) + parseFloat(row.fee, 10))}`,
        title: row => `$${formatPrice(parseFloat(row.amount, 10) + parseFloat(row.fee, 10))}`,
      });
    } else {
      fields.push({
        label: formatMessage('NET'),
        name: 'net',
        render: row => (
          <span>
            <span className="net-bold">
              {`$${formatPrice(row.amount)} `}
            </span>
            USD
          </span>),
        title: row => `$${formatPrice(row.amount)} USD`,
      });
      fields.push({
        label: formatMessage('Amount'),
        name: 'amount',
        render: row => `$${formatPrice(parseFloat(row.amount, 10) + parseFloat(row.fee, 10))}`,
        title: row => `$${formatPrice(parseFloat(row.amount, 10) + parseFloat(row.fee, 10))}`,
      });
    }

    fields.push({
      label: formatMessage('FEE'),
      name: 'fee',
      render: row => `$${formatPrice(row.fee)}`,
      title: row => `$${formatPrice(row.fee)}`,
    });
    fields.push({
      label: formatMessage('Payment Status'),
      name: 'status',
      render: row => row.status || '-',
      title: row => row.status || '-',
    });

    if (transactionsRequesting) {
      return null;
    }

    return (
      <Paper loading={transactionsRequesting}>
        <SearchComponent
          cancelSearch={this.onCancelSearch}
          placeholder={formatMessage('Transfers')}
          search={this.onSearchText}
          searchText={searchText}
        />
        <div className="payments-page">
          <HeaderTitle>
            {formatMessage('Transfers')}
          </HeaderTitle>
          {
            transactionsFiltered.length ? (
              <SmartTable
                className="payments-table"
                data={modifiedTransactions}
                fields={fields}
                threshold="md"
                rowClassName={(row, index) =>
                  this.getRowClassName(row, index, modifiedTransactions)}
              />) : ''
          }
        </div>
        <Dialog
          classes={{ paper: classes.dialog }}
          onClose={this.handleClose}
          open={open}
          scroll="body"
        >
          <DialogTitle classes={{ root: classes.title }}>
            {dialogTitle}
          </DialogTitle>
          <DialogContent className="order-detail-dialog">
            {orderRequesting && (
              <div className="customer-progress">
                <CircularProgress />
              </div>
            )}
            <InvoiceDetail
              editDisabled
              formatMessage={formatMessage}
              order={order}
              readOrder={this.readOrder}
              updateOrder={updateOrder}
            />
          </DialogContent>
          <DialogActions>{actions}</DialogActions>
        </Dialog>
      </Paper>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('orders', 'order')(state, 'order'),
  ...selectState('transactions', 'transactions')(state, 'transactions'),
});

export default compose(
  setDisplayName('Transactions'),
  connect(
    mapStateToProps,
    dispatchRestMaker([
      [ordersActions, 'order'],
      [transactionsActions, 'transaction'],
    ]),
  ),
  withStyles(styles),
  immutableToJS,
  injectIntl,
)(Transactions);
